/**
 * P419.1 -- record a scenario test, and read the coverage it moves.
 *
 * P419 shipped the engine that counts testing the way a regulator
 * counts it, and no way to record a test. This is that screen, and it
 * puts the grid beside the form on purpose: the point of recording a
 * run is what it does to the coverage, and a form that says "saved"
 * and leaves the reader to go and look has hidden the answer.
 *
 * The grid's four states are the phase's whole argument. `planned` is
 * not `tested`, because a diary entry is not a test. `stale` is not
 * `tested`, because a test nobody repeated is not evidence of a
 * current capability. Before P419 all four read the same: covered.
 */
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { formatDate } from "shared/time/formatDate";

import { ModuleRubric } from "shared/layout/ModuleHomeTile";
import { canRunOperations } from "shared/api/authority";
import { useMe } from "shared/api/useMe";
import { useToast } from "shared/toast/ToastProvider";
import { useScenarios } from "twin/api/scenario_hooks";

import { useCoverage, useExecuteRun, useRecordRun, useTestRuns } from "./testRunHooks";

const METHODS = ["tabletop", "simulation", "live_systems"] as const;
const INTENSITIES = ["light", "standard", "severe"] as const;

/** The four states, in the order a reader should take them: what is
 *  done, what has aged, what is booked, what is not. */
const STATE_TONE: Record<string, string> = {
  tested: "bg-rag-green/15 text-rag-green",
  stale: "bg-rag-amber/15 text-rag-amber",
  planned: "bg-slate-100 text-slate-600",
  untested: "bg-rag-red/10 text-rag-red",
};

export function TestRunsView() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  // Round 14 D4 -- the Resilience Lead records her own tests; the
  // endpoints admit her, and the form was admin-only.
  const isAdmin = canRunOperations(useMe().data);
  const [regime, setRegime] = useState("sama");
  // Round 20 Y6 -- which run is open to its facilitator and participants.
  const [openRun, setOpenRun] = useState<string | null>(null);
  // Round 16 D22 -- reached from a scenario's own page, the form starts
  // on that scenario.
  const [params] = useSearchParams();
  const [scenarioId, setScenarioId] = useState(params.get("scenario") ?? "");
  const [method, setMethod] = useState<string>("tabletop");
  const [intensity, setIntensity] = useState<string>("standard");
  const [facilitator, setFacilitator] = useState("");
  const [vendor, setVendor] = useState("");

  const scenarios = useScenarios();
  const runs = useTestRuns();
  const coverage = useCoverage(regime);
  const record = useRecordRun();
  const execute = useExecuteRun();

  // Guarded the way every list on this surface is: a partial payload
  // degrades to an empty table rather than taking the page down.
  const scenarioRows = Array.isArray(scenarios.data) ? scenarios.data : [];
  const runRows = Array.isArray(runs.data) ? runs.data : [];
  const cells = Array.isArray(coverage.data?.cells) ? coverage.data.cells : [];
  const classes = [...new Set(cells.map((c) => c.scenario_class))].sort();
  const operations = [...new Set(cells.map((c) => c.ibs_service_id))];

  const onRecord = () => {
    if (!scenarioId) return;
    record.mutate(
      {
        scenario_id: scenarioId,
        method,
        intensity,
        ...(facilitator.trim() ? { facilitator_email: facilitator.trim() } : {}),
        // A vendor participant is what "with third parties where
        // possible" asks about, and it is the only participant kind
        // this form asks for by name because it is the only one a
        // check reads.
        ...(vendor.trim()
          ? { participants: [{ kind: "vendor", name: vendor.trim() }] }
          : {}),
        regime_code: regime,
      },
      {
        onSuccess: () =>
          toast.push({
            tone: "success",
            message: t("modules.twin.testRuns.recorded"),
          }),
        onError: () =>
          toast.push({
            tone: "error",
            message: t("modules.twin.testRuns.recordFailed"),
          }),
      },
    );
  };

  return (
    <article className="space-y-4" data-testid="test-runs-view">
      <header className="space-y-1">
        <ModuleRubric>{t("modules.twin.testRuns.rubric")}</ModuleRubric>
        <h1 className="font-display text-2xl text-slate-900">
          {t("modules.twin.testRuns.heading")}
        </h1>
        <p className="max-w-3xl text-sm text-slate-600">
          {t("modules.twin.testRuns.body")}
        </p>
      </header>

      {isAdmin && (
        <section className="space-y-2 rounded border border-slate-200 bg-white p-3">
          <h2 className="text-sm font-semibold text-slate-800">
            {t("modules.twin.testRuns.recordHeading")}
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            <select
              aria-label={t("modules.twin.testRuns.scenario")}
              data-testid="test-run-scenario"
              value={scenarioId}
              onChange={(e) => setScenarioId(e.target.value)}
              className="rounded border border-slate-300 px-2 py-1 text-sm"
            >
              <option value="">{t("modules.twin.testRuns.pickScenario")}</option>
              {scenarioRows.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <select
              aria-label={t("modules.twin.testRuns.method")}
              data-testid="test-run-method"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="rounded border border-slate-300 px-2 py-1 text-sm"
            >
              {METHODS.map((m) => (
                <option key={m} value={m}>
                  {t(`modules.twin.testRuns.methods.${m}`)}
                </option>
              ))}
            </select>
            <select
              aria-label={t("modules.twin.testRuns.intensity")}
              data-testid="test-run-intensity"
              value={intensity}
              onChange={(e) => setIntensity(e.target.value)}
              className="rounded border border-slate-300 px-2 py-1 text-sm"
            >
              {INTENSITIES.map((i) => (
                <option key={i} value={i}>
                  {t(`modules.twin.testRuns.intensities.${i}`)}
                </option>
              ))}
            </select>
            <input
              aria-label={t("modules.twin.testRuns.facilitator")}
              data-testid="test-run-facilitator"
              value={facilitator}
              onChange={(e) => setFacilitator(e.target.value)}
              placeholder={t("modules.twin.testRuns.facilitator")}
              className="w-56 rounded border border-slate-300 px-2 py-1 text-sm"
            />
            <input
              aria-label={t("modules.twin.testRuns.vendorParticipant")}
              data-testid="test-run-vendor"
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              placeholder={t("modules.twin.testRuns.vendorParticipant")}
              className="w-56 rounded border border-slate-300 px-2 py-1 text-sm"
            />
            <button
              type="button"
              data-testid="test-run-record"
              disabled={!scenarioId || record.isPending}
              onClick={onRecord}
              className="rounded bg-brand-brass px-3 py-1 text-sm font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
            >
              {t("modules.twin.testRuns.record")}
            </button>
          </div>
          <p className="text-[11px] text-slate-500">
            {t("modules.twin.testRuns.recordNote")}
          </p>
        </section>
      )}

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-800">
          {t("modules.twin.testRuns.runsHeading")}
        </h2>
        {runs.isError ? (
          // ROUND 15 D17 -- "No scenario test has been recorded." under a
          // failed read is a claim about the bank, not about the request.
          <p
            role="alert"
            data-testid="test-runs-failed"
            className="text-sm text-rose-800"
          >
            {t("modules.twin.testRuns.runsFailed")}
          </p>
        ) : runRows.length === 0 ? (
          <p data-testid="test-runs-empty" className="text-sm text-slate-500">
            {t("modules.twin.testRuns.noRuns")}
          </p>
        ) : (
          <table className="w-full text-start text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="py-1">{t("modules.twin.testRuns.scenario")}</th>
                <th className="py-1">{t("modules.twin.testRuns.method")}</th>
                <th className="py-1">{t("modules.twin.testRuns.when")}</th>
                <th className="py-1">{t("modules.twin.testRuns.verdict")}</th>
                <th className="py-1" />
              </tr>
            </thead>
            <tbody>
              {runRows.map((r) => (
                <Fragment key={r.id}>
                  <tr
                    data-testid="test-run-row"
                    data-status={r.status}
                    className="border-t border-slate-100"
                  >
                    <td className="py-1.5 text-slate-700">
                      {/* Round 16 D22 -- the scenario's name, not its code.
                        Round 20 Y6 -- and the row opens. */}
                      <button
                        type="button"
                        data-testid={`test-run-open-${r.id}`}
                        aria-expanded={openRun === r.id}
                        onClick={() => setOpenRun(openRun === r.id ? null : r.id)}
                        className="text-start text-brand-brassDark hover:underline"
                      >
                        {r.scenario_name ?? r.scenario_code ?? r.scenario_id}
                      </button>
                    </td>
                    <td className="py-1.5 text-slate-600">
                      {t(`modules.twin.testRuns.methods.${r.method}`)}
                    </td>
                    <td className="py-1.5 tabular-nums text-slate-600">
                      {r.run_at.slice(0, 10)}
                      {/* Round 20 V4 -- the scenario was revised after this run. */}
                      {r.scenario_revised_after && (
                        <span
                          data-testid="test-run-revised"
                          className="block text-[10px] text-rag-amber"
                        >
                          {t("modules.twin.testRuns.staleSinceRevision", {
                            date: formatDate(r.scenario_revised_after, i18n.language),
                          })}
                        </span>
                      )}
                    </td>
                    <td className="py-1.5">
                      {r.status === "executed" ? (
                        <span className="text-slate-700">
                          {r.verdict_summary ?? ""}
                        </span>
                      ) : (
                        <span className="text-slate-400">
                          {t("modules.twin.testRuns.notExecuted")}
                        </span>
                      )}
                    </td>
                    <td className="py-1.5">
                      {isAdmin && r.status === "planned" && (
                        <button
                          type="button"
                          data-testid="test-run-execute"
                          onClick={() =>
                            execute.mutate(r.id, {
                              onSuccess: () =>
                                toast.push({
                                  tone: "success",
                                  message: t("modules.twin.testRuns.executed"),
                                }),
                            })
                          }
                          className="text-xs text-brand-brassDark hover:underline"
                        >
                          {t("modules.twin.testRuns.execute")}
                        </button>
                      )}
                    </td>
                  </tr>
                  {openRun === r.id && (
                    <tr data-testid={`test-run-detail-${r.id}`} className="bg-paper">
                      <td colSpan={5} className="px-2 py-2 text-xs text-slate-700">
                        <p>
                          {r.status === "planned"
                            ? t("modules.twin.testRuns.detail.booked", {
                                date: formatDate(r.run_at, i18n.language),
                              })
                            : t("modules.twin.testRuns.detail.ran", {
                                date: formatDate(r.run_at, i18n.language),
                              })}
                        </p>
                        <p>
                          {t("modules.twin.testRuns.detail.facilitator", {
                            who:
                              r.facilitator_email ??
                              t("modules.twin.testRuns.detail.notRecorded"),
                          })}
                        </p>
                        <p>
                          {t("modules.twin.testRuns.detail.participants", {
                            who:
                              (r.participants ?? []).length > 0
                                ? (r.participants ?? [])
                                    .map((p) =>
                                      t("modules.twin.testRuns.detail.participant", {
                                        name: p.name,
                                        kind: p.kind.replace(/_/g, " "),
                                      }),
                                    )
                                    .join(", ")
                                : t("modules.twin.testRuns.detail.noneRecorded"),
                          })}
                        </p>
                        <p>
                          {r.scenario_revised_after
                            ? t("modules.twin.testRuns.detail.revisedAfter", {
                                date: formatDate(
                                  r.scenario_revised_after,
                                  i18n.language,
                                ),
                              })
                            : t("modules.twin.testRuns.detail.unchanged")}
                        </p>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="space-y-2">
        <div className="flex flex-wrap items-baseline gap-2">
          <h2 className="text-sm font-semibold text-slate-800">
            {t("modules.twin.testRuns.coverageHeading")}
          </h2>
          <select
            aria-label={t("modules.twin.testRuns.regime")}
            data-testid="coverage-regime"
            value={regime}
            onChange={(e) => setRegime(e.target.value)}
            className="rounded border border-slate-300 px-2 py-0.5 text-xs"
          >
            {/* A regulator's short name is a NAME, not interface
                prose -- the same reason the RTL audit counts Latin
                names separately from composed English. Held in the
                catalogue anyway so a future regime list can come from
                the registry rather than from here. */}
            <option value="sama">{t("modules.twin.testRuns.regimes.sama")}</option>
            <option value="osfi">{t("modules.twin.testRuns.regimes.osfi")}</option>
          </select>
          {coverage.data && (
            <span data-testid="coverage-headline" className="text-xs text-slate-600">
              {t("modules.twin.testRuns.coverageHeadline", {
                pct: coverage.data.coverage_pct ?? 0,
                complete: coverage.data.operations_complete,
                total: coverage.data.operations_total,
              })}
              {coverage.data.deadline_days_remaining != null && (
                <span data-testid="coverage-deadline" className="ms-2">
                  {t("modules.twin.testRuns.deadline", {
                    days: coverage.data.deadline_days_remaining,
                    date: coverage.data.deadline ?? "",
                  })}
                </span>
              )}
              {(coverage.data.internal_functions_excluded ?? 0) > 0 && (
                <span data-testid="coverage-cif-excluded" className="ms-2">
                  {t("modules.twin.testRuns.cifExcluded", {
                    count: coverage.data.internal_functions_excluded ?? 0,
                  })}
                </span>
              )}
            </span>
          )}
        </div>
        {/* Round 14 D2 -- a failed request is a failure. The endpoint
            500'd on every call and this read "nothing to count", which
            is a claim about the bank rather than about the request. */}
        {coverage.isError ? (
          <p
            role="alert"
            data-testid="coverage-failed"
            className="text-sm text-rose-800"
          >
            {t("modules.twin.testRuns.coverageFailed")}
          </p>
        ) : coverage.isLoading ? null : cells.length === 0 ? (
          <p data-testid="coverage-empty" className="text-sm text-slate-500">
            {t("modules.twin.testRuns.noCoverage")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-start text-xs">
              <thead className="uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-1">{t("modules.twin.testRuns.operation")}</th>
                  {classes.map((c) => (
                    <th key={c} className="py-1 pe-2">
                      {t(`vocab.scenarioClass.${c}`, { defaultValue: c })}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {operations.map((id) => {
                  const row = cells.filter((c) => c.ibs_service_id === id);
                  const first = row[0];
                  if (!first) return null;
                  return (
                    <tr key={id} className="border-t border-slate-100">
                      <td className="py-1 pe-3 text-slate-700">{first.ibs_name}</td>
                      {classes.map((cls) => {
                        const cell = row.find((c) => c.scenario_class === cls);
                        const state = cell?.state ?? "untested";
                        return (
                          <td key={cls} className="py-1 pe-2">
                            <span
                              data-testid="coverage-cell"
                              data-state={state}
                              title={
                                cell?.stale_since_revision
                                  ? t("modules.twin.testRuns.staleSinceRevision", {
                                      date: formatDate(
                                        cell.stale_since_revision,
                                        i18n.language,
                                      ),
                                    })
                                  : cell?.stale_since_change
                                    ? t("modules.twin.changes.staleAfterChange", {
                                        date: formatDate(
                                          cell.stale_since_change,
                                          i18n.language,
                                        ),
                                      })
                                    : cell?.tested_at
                                      ? `${cell.tested_at.slice(0, 10)}`
                                      : undefined
                              }
                              className={`rounded px-1 py-0.5 ${STATE_TONE[state] ?? ""}`}
                            >
                              {t(`modules.twin.testRuns.states.${state}`)}
                            </span>
                            {/* Round 20 V4 -- stale because the scenario it
                                tested was revised after the run. */}
                            {cell?.stale_since_revision && (
                              <span
                                data-testid="coverage-cell-revised"
                                className="block text-[10px] text-rag-amber"
                              >
                                {t("modules.twin.testRuns.staleSinceRevision", {
                                  date: formatDate(
                                    cell.stale_since_revision,
                                    i18n.language,
                                  ),
                                })}
                              </span>
                            )}
                            {/* P421 -- why it is stale, when a change made it so. */}
                            {cell?.stale_since_change && (
                              <span
                                data-testid="coverage-cell-changed"
                                className="block text-[10px] text-rag-amber"
                              >
                                {t("modules.twin.changes.staleChangedShort", {
                                  date: formatDate(
                                    cell.stale_since_change,
                                    i18n.language,
                                  ),
                                })}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="max-w-3xl text-[11px] text-slate-500">
          {t("modules.twin.testRuns.coverageNote")}
        </p>
      </section>
    </article>
  );
}
